import { useId, useRef, useState, type ChangeEvent, type DragEvent } from 'react'

import { Icon } from './Icon'

interface FileFieldProps {
  label: string
  /**
   * Clip the label span rather than removing it. The `<input>` is named by
   * `aria-labelledby`, so an absent span leaves it with no accessible name at
   * all — the contract `SegmentedControl.tsx:51` has.
   */
  hideLabel?: boolean
  /** Shown under the control and announced with it. */
  hint?: string
  /** The line under the icon. Without it the zone is a plain field. */
  dropTitle?: string
  /** A mono footnote under the file name — what the server will accept. */
  meta?: string
  /** Sets aria-invalid and renders the message. */
  error?: string | null
  /** A HINT to the picker, never a gate. See the note below. */
  accept: string
  file: File | null
  onPick: (file: File | null) => void
  disabled?: boolean
}

/**
 * Choosing a session archive.
 *
 * ── THE REAL INPUT STAYS IN THE DOM, AND IT IS NOT `display: none` ──────────
 *
 * The visible control is a `<label for>`; the `<input type="file">` it points at
 * is hidden with `.visually-hidden`, which clips it rather than removing it from
 * the layout. `display: none` and `visibility: hidden` are the two techniques
 * that stop the picker opening at all on iOS Safari — the element has no box, the
 * tap on the label has nothing to forward to, and nothing happens. There is no
 * error and no console message; the button simply does not work, on one platform,
 * which is why it is a device-checklist row rather than something a test catches.
 *
 * `opacity: 0` would also work and is NOT used: `scripts/audit-css.mjs` check C
 * flags an `opacity: 0` rule that does not also set `pointer-events: none`, and
 * setting that here would break the very forwarding this depends on.
 *
 * The v3 canvas replaces the label with `<button>Choose a file</button>` inside a
 * `.dropzone` wrapper. Neither is adopted (R11): a button has nothing to forward
 * a tap to, and the dashed treatment lands on `.filefield` itself, so the clipped
 * input keeps a positioned ancestor and the two `:has()` rules below keep
 * resolving. Only the state class `dropzone--active` comes across.
 *
 * ── AND `accept` IS A HINT, NOT A GATE ──────────────────────────────────────
 *
 * iOS has no reliable UTI for `.tar.gz`, so an `accept` list can grey out the
 * file a user actually wants. The list is deliberately generous, and the
 * extension is re-checked after selection by the caller — with a readable
 * message, rather than the file being silently ignored.
 *
 * ── DRAG AND DROP FEEDS THE SAME `onPick` ───────────────────────────────────
 *
 * There is exactly one way in. A drop calls the caller's `onPick`, so the key
 * minting, the extension check and the grant-size check on the other side all
 * run unchanged; a second validation path is how one of them ends up skipped.
 * The zone takes no `role`, no `tabIndex` and no live region: dragging is a
 * pointer enhancement and the label is the keyboard path.
 */
export function FileField({
  label,
  hideLabel = false,
  hint,
  dropTitle,
  meta,
  error,
  accept,
  file,
  onPick,
  disabled = false,
}: FileFieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const metaId = `${id}-meta`
  const errorId = `${id}-error`
  const nameId = `${id}-name`
  const inputRef = useRef<HTMLInputElement>(null)
  /**
   * A COUNTER, not a boolean.
   *
   * `dragleave` fires every time the pointer crosses into a child element, so a
   * boolean turns the dashed highlight off the moment the cursor passes over
   * the icon or the trigger — a flicker for the whole of the drag.
   */
  const depth = useRef(0)
  const [active, setActive] = useState(false)

  const describedBy = [
    hint ? hintId : null,
    meta ? metaId : null,
    error ? errorId : null,
    file ? nameId : null,
  ]
    .filter(Boolean)
    .join(' ')

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    onPick(event.target.files?.[0] ?? null)
  }

  const clear = () => {
    onPick(null)
    // The input keeps its own value, and it is the thing that decides whether
    // picking the SAME file again fires a change event. Without this, choosing
    // a file, clearing it, and choosing it again does nothing at all.
    if (inputRef.current !== null) inputRef.current.value = ''
  }

  const onDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (disabled) return
    depth.current += 1
    setActive(true)
  }

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    // ALWAYS, disabled or not. Without a cancelled `dragover` the browser takes
    // the drop itself and NAVIGATES to the file, which kills an upload in
    // flight -- the one moment the control is disabled.
    event.preventDefault()
    event.dataTransfer.dropEffect = disabled ? 'none' : 'copy'
  }

  const onDragLeave = () => {
    if (disabled) return
    depth.current -= 1
    if (depth.current === 0) setActive(false)
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    depth.current = 0
    setActive(false)
    if (disabled) return
    const dropped = event.dataTransfer.files?.[0] ?? null
    if (dropped === null) return
    // Same reason `clear()` does it: the input's own value decides whether
    // picking that file through the picker afterwards fires a change event.
    if (inputRef.current !== null) inputRef.current.value = ''
    onPick(dropped)
  }

  return (
    <div
      className={['field', 'filefield', active && 'dropzone--active'].filter(Boolean).join(' ')}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <span className={hideLabel ? 'visually-hidden' : 'field__label'} id={`${id}-label`}>
        {label}
      </span>
      {/* `aria-labelledby` is REQUIRED here, not decorative. The visible label
          is a `<span>` (the `<label htmlFor>` below is the trigger and carries
          its own words), so without this the input has no accessible name at
          all and is announced as "file, button" with nothing to say what file. */}
      <input
        ref={inputRef}
        id={id}
        type="file"
        className="visually-hidden filefield__input"
        accept={accept}
        disabled={disabled}
        onChange={onChange}
        aria-labelledby={`${id}-label`}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      />
      <Icon name="upload" className="filefield__icon" />
      {dropTitle !== undefined && <strong className="filefield__title">{dropTitle}</strong>}
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      <div className="filefield__row">
        <label className="btn btn--secondary btn--sm filefield__label" htmlFor={id}>
          {file ? 'Choose a different file' : 'Choose a file'}
        </label>
        {file !== null && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            // Disabled with the rest of the form. It is a real `<button>`, so it
            // honours this -- unlike the `<label>` beside it, which can never
            // match `.btn:disabled` and needs the CSS below instead.
            disabled={disabled}
            onClick={clear}
          >
            Clear
          </button>
        )}
      </div>
      {file !== null && (
        <p className="filefield__name product-id" id={nameId}>
          {file.name}
        </p>
      )}
      {meta !== undefined && (
        <span className="filefield__meta" id={metaId}>
          {meta}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  )
}
